<!--
  Plan 2 (storefront buyer completion) — session-gated entry surface.

  Wraps EVERY shopper page under `/shop/:slug` -- Home, Search,
  Category, Product, and (since 2026-10-02) cart, sign-in, orders, order
  detail, confirmation, addresses, payment methods and profile -- and
  establishes the shopper session BEFORE the wrapped page renders, via
  router/ensureStorefrontSession.js. Each child route states what it needs
  in `meta.session` ("anonymous-ok" or "account"); the account pages'
  own guard (router/storefrontAccountGuard.js) sends a visitor without a
  personal session to the store's home. The payment-remediation route
  stays OUTSIDE: its emailed token is the credential. The emailed ORDER
  link (orders/:id?token=) is inside but never refused -- its one-order
  code is the credential, so a closed store renders it with no session. The
  order pages themselves (orders, orders/:id) render the same way for a
  closed or archived store: a signed-in shopper sees their own orders,
  anyone else the order-link form (router/ownOrdersSession.js).
  Plan: docs/planning/2026-10-02-storefront-session-everywhere-plan.md

  The shared-PASSWORD wall is NOT here. It lives in the router guard
  `router/storefrontPasswordGate.js`, which covers the whole
  `/shop/:slug/*` tree by path, including the password page itself.

  The token-swap sequence lives in router/anonymousStorefrontMint.js,
  shared with the auth interceptor's mid-visit recovery -- including the
  window._swappingToken guard around the swap, which suppresses the global
  401 interceptor while localStorage briefly holds a half-swapped identity
  (PR #451; see project_storefront-session-architecture.md).
  TheLoginPage's loginShopperFromURL is still a separate copy; converging
  it is part of the post-beta public-first storefront refactor.
-->
<template>
  <div class="storefront-session-gate" data-testid="storefront-session-gate">
    <div
      v-if="gateState === 'invalid'"
      class="gate-invalid"
      data-testid="session-gate-invalid"
    >
      <h2>Store not found</h2>
      <p>Please make sure your url is correct.</p>
    </div>

    <!--
      The store exists and is CLOSED to new shoppers: a Pop-Up past its end
      time (Jake, 2026-10-02), or a store that launched and was closed again
      (card #335, Jake 2026-10-24). It shows ONLY what the backend's closed
      notice carries: the store's name, its logo and its contact address. A
      shopper already holding a session for the store never reaches this: the
      held session is kept, so a checkout under way can finish.
    -->
    <div
      v-else-if="gateState === 'closed'"
      class="gate-closed"
      data-testid="session-gate-closed"
    >
      <img
        v-if="closedLogoUrl"
        :src="closedLogoUrl"
        :alt="closedNotice.storeName ? `${closedNotice.storeName} logo` : 'Store logo'"
        class="gate-closed__logo"
        data-testid="session-gate-closed-logo"
      />
      <h2 data-testid="session-gate-closed-title">
        {{
          closedNotice.storeName
            ? `${closedNotice.storeName} is currently closed`
            : "This store is currently closed"
        }}
      </h2>
      <p>It is not taking orders right now.</p>
      <p
        v-if="closedContactEmail"
        class="gate-closed__contact"
        data-testid="session-gate-closed-contact"
      >
        Questions? Email
        <a
          :href="`mailto:${closedContactEmail}`"
          data-testid="session-gate-closed-email"
        >{{ closedContactEmail }}</a>
      </p>
    </div>

    <!--
      Anything that is NOT "this store does not exist" lands here: a 429, a
      5xx, a network failure, a browser that blocks storage, a bug in this
      component. Jake, 2026-10-01: "Store not found is the right message to
      send an attacker, but never the right message to send to an anonymous
      user." The button re-runs entry in place -- no reload, so no loop.
    -->
    <div
      v-else-if="gateState === 'unavailable'"
      class="gate-unavailable"
      data-testid="session-gate-unavailable"
    >
      <h2>We're having trouble opening the store</h2>
      <p>Please try again in a moment.</p>
      <button
        type="button"
        class="gate-retry"
        data-testid="session-gate-retry"
        @click="retry"
      >
        Try again
      </button>
    </div>

    <div
      v-else-if="gateState === 'loading'"
      class="gate-loading"
      data-testid="session-gate-loading"
    >
      Loading storefront. Please wait...
    </div>

    <template v-else>
      <!--
        A popped-out store-editor PREVIEW (?preview=1 in its own tab) has no
        drawer chrome around it, so the storefront says it is a preview here
        (Jake, 2026-10-22), and "not yet launched" when the backend reports
        the store has not launched. Inside the editor's iframe the drawer
        chrome already carries both, so nothing renders there.
      -->
      <div
        v-if="previewBanner"
        class="gate-preview-banner"
        role="status"
        data-testid="storefront-preview-banner"
      >
        <strong>PREVIEW</strong>
        <template v-if="previewBanner.notLaunched">
          - <span data-testid="storefront-preview-not-launched">not yet launched</span>
        </template>
        <span class="gate-preview-banner__note">
          Cart and checkout are disabled in a preview.
        </span>
      </div>
      <router-view />
    </template>
  </div>
</template>

<script>
import { describeError } from "@/utils/safeErrorLog";
import { resolveStorefrontSlug } from "@/router/storefrontSlug";
import { isStorefrontPreview, isFramedWindow } from "@/router/storefrontPreview";
// The session step itself -- mint, restore, the two-tab join, the closed
// check, the resume code -- is ONE function shared by every shopper page.
// This component owns only what the visitor SEES while it runs.
import {
  ensureStorefrontSession,
  isEmailedOrderLink,
  closedNoticeFrom,
  StoreNotFound,
  StoreClosed,
} from "@/router/ensureStorefrontSession";
import { buildImageUrl } from "@/utils/imageBaseUrl";
import { mintRequestConfig } from "@/router/anonymousStorefrontMint";
import { STOREFRONT_NO_STORE_SESSION, isOrderPage } from "@/router/ownOrdersSession";
import {
  resumeCodeFromHash,
  writeResumeFragment,
} from "@/router/storefrontResumeCode";
import { useStorefrontChrome } from "@/composables/useStorefrontChrome";
import { computed } from "vue";
import {
  STOREFRONT_STORE_ID,
  publishGateStore,
  withdrawGateStore,
} from "@/router/storefrontStoreId";

// A 429 is retried ONCE by itself, after the server's Retry-After. Missing or
// unparseable: a short default. Capped so a hostile header cannot park the
// visitor indefinitely -- they still have the button.
const DEFAULT_RETRY_AFTER_MS = 2000;
const MAX_RETRY_AFTER_MS = 60000;

function retryAfterMs(err) {
  const headers = err?.response?.headers || {};
  const raw =
    typeof headers.get === "function"
      ? headers.get("retry-after")
      : headers["retry-after"] ?? headers["Retry-After"];
  if (raw == null || raw === "") return DEFAULT_RETRY_AFTER_MS;
  const seconds = Number(raw);
  let ms = Number.isFinite(seconds)
    ? seconds * 1000
    : Date.parse(String(raw)) - Date.now();
  if (!Number.isFinite(ms) || ms < 0) ms = DEFAULT_RETRY_AFTER_MS;
  return Math.min(ms, MAX_RETRY_AFTER_MS);
}

const isStatus = (err, status) => err?.response?.status === status;

const EMPTY_NOTICE = Object.freeze({ storeName: null, logoKey: null, contactEmail: null });

// The closed notice's logo: the store's brand image, resolved the way the
// storefront topbar resolves it (a bare key onto the image base, an http(s)
// URL as is). Anything else carrying a scheme (javascript:, data:, a seeder's
// stub://) renders no image rather than a dangerous or broken src.
function closedLogoUrlFor(key) {
  if (!key) return null;
  if (/^https?:\/\//i.test(key)) return key;
  if (/^[a-z][a-z0-9+.-]*:/i.test(key) || key.startsWith("//")) return null;
  return buildImageUrl(key);
}

// The closed notice's contact: one bare address, or nothing. The backend
// already sends only a validated reply-to; this is the second check at the
// sink, so a value carrying mailto: header characters (?, &, #) or spaces
// never becomes an href.
const BARE_EMAIL_RE = /^[^\s@?&#%/:<>"]+@[^\s@?&#%/:<>"]+\.[^\s@?&#%/:<>"]+$/;

// Pages that render with NO store session where the store is closed, switched
// off, archived or unresolvable: the emailed order link (its one-order code
// is the credential) and the order pages (OD-20261016-3: a signed-in shopper
// keeps their own orders, anyone else gets the order-link form --
// router/ownOrdersSession.js).
const rendersWithoutSession = (route) =>
  isEmailedOrderLink(route) || isOrderPage(route);

export default {
  name: "StorefrontSessionGate",

  // WHICH STORE every wrapped page shows: the store id this gate resolved
  // from the URL slug (`campaignID`), and nothing else. Provided rather than
  // copied into storage so there is ONE source -- the gate's own state -- and
  // so a privileged session, which must not write the shared
  // `localStorage.campaignId` (cross-tab logout bug), gets it too. Pages
  // render only once the gate is 'ready', so the id is set when they mount.
  // See router/storefrontStoreId.js.
  provide() {
    return {
      [STOREFRONT_STORE_ID]: computed(() => this.campaignID),
      // The page was rendered with no store session (router/ownOrdersSession.js).
      [STOREFRONT_NO_STORE_SESSION]: computed(() => this.sessionDeferred),
    };
  },

  data() {
    return {
      // 'loading' | 'invalid' | 'closed' | 'unavailable' | 'ready'
      gateState: "loading",
      campaignID: null,
      // The closed screen's notice (`closedNoticeFrom`): name, logo, contact.
      closedNotice: EMPTY_NOTICE,
      // The one automatic 429 retry has been spent (reset by the button).
      autoRetried: false,
      retryTimer: null,
      // A closed (or inactive) store's emailed order link was rendered with
      // NO session. Leaving it for another page re-runs entry then.
      sessionDeferred: false,
      // { notLaunched } while a popped-out preview tab shows its banner.
      previewBanner: null,
      // Bumped by every establish(); a run whose number is no longer current
      // (the slug changed while it was in flight) leaves the state alone.
      establishGeneration: 0,
    };
  },

  computed: {
    closedLogoUrl() {
      return closedLogoUrlFor(this.closedNotice.logoKey);
    },
    closedContactEmail() {
      const email = this.closedNotice.contactEmail;
      return email && BARE_EMAIL_RE.test(email) ? email : null;
    },
  },

  watch: {
    // Publish the provided id to the chrome above the gate (the layout's
    // cart badge) -- the SAME value, for this slug. Null while entry runs.
    campaignID() {
      this.publish();
    },
    // Whether the page was admitted without a session rides along with the
    // published id, so the chrome knows not to read store content for it.
    sessionDeferred() {
      this.publish();
    },
    "$route.params.slug"(newSlug, oldSlug) {
      if (newSlug && newSlug !== oldSlug) {
        // A different store is a fresh entry: its 429 gets its own
        // automatic retry.
        this.retry();
      }
    },
    "$route.fullPath"() {
      if (this.sessionDeferred && !rendersWithoutSession(this.$route)) {
        this.retry();
      }
    },
  },

  async mounted() {
    await this.establish();
  },

  beforeUnmount() {
    clearTimeout(this.retryTimer);
    if (this.campaignID != null) withdrawGateStore();
  },

  methods: {
    publish() {
      if (this.campaignID == null) {
        withdrawGateStore();
      } else {
        publishGateStore(resolveStorefrontSlug(this.$route), this.campaignID, {
          sessionless: this.sessionDeferred,
        });
      }
    },

    retry() {
      this.autoRetried = false;
      return this.establish();
    },

    /**
     * Map a failed entry onto what the visitor is told.
     *
     * "Store not found" ONLY when a store-existence step said so
     * (`StoreNotFound`). Everything else is the retryable state, because
     * none of it says the store is not there -- including a request that
     * timed out (storefront requests time out at ~20s, see @/axios), so a
     * slow network shows "try again" rather than a page that never loads.
     */
    async handleFailure(err) {
      if (err instanceof StoreNotFound) {
        this.gateState = "invalid";
        return;
      }
      if (err instanceof StoreClosed) {
        if (err.notice === undefined) {
          await this.showClosed(resolveStorefrontSlug(this.$route));
        } else {
          this.closedNotice = err.notice;
          this.gateState = "closed";
        }
        return;
      }
      this.gateState = "unavailable";
      if (isStatus(err, 429) && !this.autoRetried) {
        this.autoRetried = true;
        clearTimeout(this.retryTimer);
        this.retryTimer = setTimeout(() => this.establish(), retryAfterMs(err));
      }
    },

    /**
     * The closed screen after a token door said "closed" without a notice:
     * look the notice (name, logo, contact) up. It is a nicety -- if the
     * lookup fails the screen still shows, unnamed.
     */
    async showClosed(slug) {
      this.closedNotice = EMPTY_NOTICE;
      try {
        const resp = await this.$axios.get(
          `/campaign/getIsStoreClosed/${slug}`,
          mintRequestConfig()
        );
        if (resp?.data?.closed) this.closedNotice = closedNoticeFrom(resp.data);
      } catch {
        // Unnamed closed screen.
      }
      this.gateState = "closed";
    },

    async establish() {
      const generation = (this.establishGeneration || 0) + 1;
      this.establishGeneration = generation;
      const stale = () => generation !== this.establishGeneration;
      clearTimeout(this.retryTimer);
      this.gateState = "loading";
      // A new entry (a slug change, a retry) forgets the previous store.
      this.campaignID = null;
      this.previewBanner = null;
      const slug = resolveStorefrontSlug(this.$route);
      if (!slug) {
        this.gateState = "invalid";
        return;
      }

      const sessionlessOk = rendersWithoutSession(this.$route);
      try {
        const result = await ensureStorefrontSession({
          slug,
          axiosInstance: this.$axios,
          store: this.$store,
          preview: isStorefrontPreview(this.$route),
          resumeCode: resumeCodeFromHash(this.$route?.hash),
          sessionlessOk,
        });
        if (stale()) return;
        if (result.status !== "ready") {
          this.gateState = "invalid";
          return;
        }
        this.campaignID = result.campaignId;
        this.sessionDeferred = result.sessionless === true;
        // The branding read needs a session; without one its 401 would end
        // the visit on the store home.
        if (!this.sessionDeferred) this.loadBranding(slug);
        this.loadPreviewBanner(slug, stale);
        // A freshly minted (or redeemed) anonymous session's resume code goes
        // into the address bar now: the router's own afterEach has already
        // run for this navigation.
        writeResumeFragment();
        this.gateState = "ready";
      } catch (err) {
        if (stale()) return;
        // A closed store is an expected outcome, not a failure to log.
        if (!(err instanceof StoreClosed)) {
          // eslint-disable-next-line no-console
          console.error("Storefront session gate failed", describeError(err));
        }
        await this.handleFailure(err);
      }
    },

    /**
     * The PREVIEW banner of a popped-out preview tab. Only for a preview in
     * its own tab: the editor's iframe has the drawer chrome instead, and a
     * shopper visit is not a preview. "Not yet launched" is the backend's
     * answer (getCampaignPreviewInfo, the server-owned preview decision), the
     * same `active` the drawer badge reads. Not awaited: the banner never
     * holds up the page, and a failed lookup still shows "PREVIEW".
     */
    async loadPreviewBanner(slug, stale) {
      if (!isStorefrontPreview(this.$route) || isFramedWindow()) return;
      this.previewBanner = { notLaunched: false };
      try {
        const resp = await this.$axios.get(
          `/campaign/getCampaignPreviewInfo/${slug}`,
          { ...mintRequestConfig(), params: { preview: "1" } }
        );
        if (stale()) return;
        const info = resp?.data;
        if (info?.can_preview && info.active === false) {
          this.previewBanner = { notLaunched: true };
        }
      } catch {
        // The plain PREVIEW banner stays.
      }
    },

    /**
     * Brand the shell (store name, color, logo, white label) for a SESSION
     * page -- cart, sign-in, orders, account. The browse pages (Home,
     * Category, Product, Search) load the same chrome themselves because they
     * also need its hero and welcome content; loading it here too would
     * double every request. A route says which it is with `meta.session`.
     * Not awaited: branding never holds up the page.
     */
    loadBranding(slug) {
      if (!this.$route?.meta?.session) return;
      useStorefrontChrome().fetchChrome(slug);
    },
  },
};
</script>

<style scoped>
.gate-loading,
.gate-invalid,
.gate-closed,
.gate-unavailable {
  padding: 64px 24px;
  text-align: center;
  color: #444;
}

.gate-invalid h2,
.gate-closed h2,
.gate-unavailable h2 {
  margin: 0 0 8px 0;
  font-size: 22px;
}

.gate-invalid p,
.gate-closed p,
.gate-unavailable p {
  margin: 0;
  color: #777;
}

.gate-closed__logo {
  display: block;
  max-width: 220px;
  max-height: 120px;
  margin: 0 auto 24px;
  object-fit: contain;
}

.gate-closed p.gate-closed__contact {
  margin-top: 16px;
}

.gate-preview-banner {
  padding: 8px 16px;
  text-align: center;
  font-size: 14px;
  background: var(--tf-warning-soft, #fff4e5);
  color: var(--tf-warning-fg, #7a4b00);
}

.gate-preview-banner__note {
  margin-left: 8px;
}

.gate-retry {
  margin-top: 20px;
  padding: 10px 24px;
  border: 0;
  border-radius: 6px;
  background: #119ae9;
  color: #fff;
  font-size: 16px;
  cursor: pointer;
}
</style>
